import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AssetsIcon, ElementsIcon, HelpIcon, InspectionsIcon, ReportBlockIcon, ReportIcon, TestSuitesIcon } from '@/components/raytd/icons';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import classNames from 'classnames';
import { PanelsTopLeft } from 'lucide-react';
import { useSidebarContext } from '@/app/layout2/Sidebar';
import { ChatBubbleIcon } from '@radix-ui/react-icons';
import { Restricted } from '@/components/raytd/Restricted';

const isActiveRoute = (path: string) => {
  if (path === '/') {
    // For home route, it should only be active if it's exactly '/'
    return location.pathname === '/';
  }
  // For other routes, check if the pathname starts with the given path
  return location.pathname.startsWith(path);
};

interface HeaderProps {
  title: string;
}

const Header: React.FC<HeaderProps> = ({ title }) => {
  const { showSidebar } = useSidebarContext();

  return (
    <div className={cn(
      "flex items-center font-semibold rounded-lg px-3 py-2 text-muted-foreground transition-all duration-200 ease-in-out overflow-hidden",
      {
        "ml-7": showSidebar,
      }
    )}>
      <span className={cn(
        "transition-all duration-200 ease-in-out whitespace-nowrap",
        showSidebar ? "opacity-100 w-auto" : "opacity-0 w-0"
      )}>
        {title}
      </span>
      <span className={cn(
        "absolute transition-all duration-200 ease-in-out",
        !showSidebar ? "opacity-100" : "opacity-0"
      )}>
        {title.substring(0, 1)}
      </span>
    </div>
  );
};

interface SidebarNavigationProps {
  showSidebar: boolean;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({ showSidebar }) => {
  const location = useLocation();

  return (
    <nav className="flex flex-col items-start px-2 text-sm font-medium lg:px-4 overflow-hidden">
      <SideBarItem icon={<PanelsTopLeft className="h-4 w-4" />} title="Dashboard" to="/" />
      <SideBarSeparator />
      <Header title="manage." />
      <SideBarItem icon={<AssetsIcon className="h-4 w-4" />} title="Assets" to="/assets" />
      <SideBarSeparator />
      <Header title="inspect." />
      <Restricted to="test_suites_module" variant="disabled">
        <SideBarItem icon={<TestSuitesIcon className="h-4 w-4" />} title="Test Suites" to="/test-suites" />
      </Restricted>
      <Restricted to="element_groups_module" variant="disabled">
        <SideBarItem icon={<ElementsIcon className="h-4 w-4" />} title="Element Groups" to="/elements" />
      </Restricted>
      <Restricted to="inspections_module" variant="disabled">
        <SideBarItem icon={<InspectionsIcon className="h-4 w-4" />} title="Inspections" to="/inspections" />
      </Restricted>

      <SideBarSeparator />
      <Header title="report." />
      <Restricted to="report_blocks_module" variant="disabled">
        <SideBarItem icon={<ReportBlockIcon className="h-4 w-4" />} title="Report Blocks" to="/report-blocks" />
      </Restricted>
      <SideBarItem icon={<ReportIcon className="h-4 w-4" />} title="Reports" to="/reports" />
      <SideBarSeparator spacing="lg" />
      <Header title="support." />
      <SideBarItem icon={<HelpIcon className="h-4 w-4" />} title="Support" to="https://support.raytd.com" external />
      {/* Intercom chat launcher - automatically opens Intercom Messenger when clicked */}
      <a
        id="support-link"
        href="#support"
        aria-label="Open support chat"
        className="flex items-center gap-3 rounded-md px-3 py-1 text-muted-foreground transition-all duration-200 hover:text-primary"
      >
        <span className="flex-shrink-0 transition-all duration-200 ease-in-out">
          <ChatBubbleIcon className="h-4 w-4" />  
        </span>
        <span className={cn(
          "whitespace-nowrap transition-all duration-200 ease-in-out",
          showSidebar ? "opacity-100 max-w-[200px]" : "opacity-0 max-w-0"
        )}>
          Live Chat
        </span>
      </a>
    </nav>
  );
};

export default SidebarNavigation;

type SidebarItemProps = {
  icon: React.ReactNode;
  title: string;
  to: string;
  badge?: React.ReactNode;
  external?: boolean;
};

const SideBarItem: React.FC<SidebarItemProps> = ({ icon, title, to, badge, external = false }) => {

  const isActivePath = isActiveRoute(to);
  const { showSidebar } = useSidebarContext();

  const linkClasses = classNames(
    "flex items-center gap-3 rounded-md px-3 py-1 text-muted-foreground transition-all duration-200 hover:text-primary",
    "w-full min-w-0 overflow-hidden",
    {
      "text-primary bg-zinc-100": isActivePath,
    }
  );

  const content = (
    <>
      <span className="flex-shrink-0 transition-all duration-200 ease-in-out">
        {icon}
      </span>
      <span className={cn(
        "whitespace-nowrap transition-all duration-200 ease-in-out",
        showSidebar ? "opacity-100 max-w-[200px]" : "opacity-0 max-w-0"
      )}>
        {title}
      </span>
      {badge && (
        <Badge className={cn(
          "ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all duration-200 ease-in-out",
          showSidebar ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )}>
          {badge}
        </Badge>
      )}
    </>
  );

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClasses}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={to}
      className={linkClasses}
    >
      {content}
    </Link>
  );
}

interface SideBarSeparatorProps {
  className?: string;
  spacing?: 'sm' | 'md' | 'lg';
}

const SideBarSeparator: React.FC<SideBarSeparatorProps> = ({ 
  className,
  spacing = 'md' 
}) => {
  const spacingClasses = {
    sm: 'my-2',
    md: 'my-4',
    lg: 'my-6'
  };

  return (
    <div className="flex w-full px-6">
      <hr 
        className={cn(
          "w-full border-none h-px bg-zinc-200/50 inset-1",
          spacingClasses[spacing],
          className
        )}
      />
    </div>
  );
}